import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sleep(ms: number = 1000) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Rút gọn email hiển thị: chỉ lấy phần trước dấu `@` (vd `thanhdt@vietmap.vn` → `thanhdt`). */
export function shortEmail(email: string | null | undefined): string {
  if (!email) return "";
  return email.split("@")[0];
}

/**
 * Convert a backend timestamp to a Date. The backend may send .NET ticks
 * (100ns since 0001-01-01), Unix milliseconds, or Unix seconds, so we detect
 * the format by magnitude. Returns null for empty/invalid values.
 */
export function ticksToDate(ticks: number | null | undefined): Date | null {
  if (!ticks || ticks <= 0) return null;
  if (ticks > 1e17) return new Date((ticks - 621355968000000000) / 10000); // .NET ticks
  if (ticks > 1e12) return new Date(ticks); // Unix milliseconds
  return new Date(ticks * 1000); // Unix seconds
}

export function formatNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined) return "-";

  let numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Round to avoid -0 issue when formatting with 0 fraction digits
  numericValue = Math.round(numericValue);
  if (numericValue === 0) numericValue = 0;

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(numericValue);
}

/** Số dạng gọn cho trục/nhãn chart: 1200 -> 1.2K, 1500000 -> 1.5M. */
export function compactNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined) return "-";
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return "-";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string = "VND"
) {
  if (amount === null || amount === undefined) return "-";

  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: currency,
  }).format(numericAmount);
}

/**
 * Generates page numbers for pagination with ellipsis
 * @param currentPage - Current page number (1-based)
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and ellipsis strings
 *
 * Examples:
 * - Small dataset (≤5 pages): [1, 2, 3, 4, 5]
 * - Near beginning: [1, 2, 3, 4, '...', 10]
 * - In middle: [1, '...', 4, 5, 6, '...', 10]
 * - Near end: [1, '...', 7, 8, 9, 10]
 */
export function getPageNumbers(currentPage: number, totalPages: number) {
  const maxVisiblePages = 5; // Maximum number of page buttons to show
  const rangeWithDots = [];

  if (totalPages <= maxVisiblePages) {
    // If total pages is 5 or less, show all pages
    for (let i = 1; i <= totalPages; i++) {
      rangeWithDots.push(i);
    }
  } else {
    // Always show first page
    rangeWithDots.push(1);

    if (currentPage <= 3) {
      // Near the beginning: [1] [2] [3] [4] ... [10]
      for (let i = 2; i <= 4; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      // Near the end: [1] ... [7] [8] [9] [10]
      rangeWithDots.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
    } else {
      // In the middle: [1] ... [4] [5] [6] ... [10]
      rangeWithDots.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    }
  }

  return rangeWithDots;
}
